<template>
<div>
  <p>L'export contiendra:
    <ul>
      <li>Nombre d’activités (Etablissement + PAT Games) proposées et réalisées (deux colonnes), tout métier confondu</li>
      <li>Nombre d’activités planifiées hors PAT Games</li>
      <li>Taux de satisfaction (bien-être) des résidents ayant participé aux activités proposées</li>
      <li>Taux de participation effectif de chaque résident aux activités planifiées > pour chaque résident taux de participation global</li>
    </ul>
  </p>
  <p>
    Veuillez choisir les dates de l'export :
    <b-row>
      <b-col>Début : <b-form-datepicker v-model="start" :max="end"></b-form-datepicker></b-col>
      <b-col>Fin : <b-form-datepicker v-model="end" :min="start"></b-form-datepicker></b-col>
    </b-row>
  </p>
  <p v-show="allEstablishments.length > 0">
    <b-form-checkbox
      id="checkbox-export-all"
      v-model="exportAll"
      name="checkbox-export-all"
      :disabled="load"
    >
      Souhaitez-vous également exporter pour les établissements du groupe ?
    </b-form-checkbox>
  </p>
  <p v-show="exportAll">
    Établissement(s) à exclure:
    <b-form-select 
      v-model="excludes" 
      :options="allEstablishments" 
      multiple 
      :select-size="4"
      :disabled="load" />
      <div>
        <b-badge v-for="esta_id in excludes" :key="'exculde-' + esta_id" style="margin-right: 5px;cursor: pointer;" @click="removeFromExclude(esta_id)">
          {{ getEstaName(esta_id) }} | x
        </b-badge>
      </div>
  </p>
  <p>
    <b-button block @click="downloadStats">
      <template v-if="load">
        <b-spinner small></b-spinner>
        {{ stepDownload }}
      </template>
      <template v-else>
        <font-awesome-icon :icon="['fas', 'download']" />
        &nbsp;{{ $t("btn.download_stat_esta") }}
      </template>
    </b-button>
  </p>
</div>
</template>

<script>
import { statForEstablishmentParticipationActivities } from '../../services/export';
import { formatStringDateBDD } from '../../misc/utils'
import { ExportStatToExcel } from '../../services/exportStatsToExcel';
import JSZip from 'jszip';
  export default {
    props: {
      establishment: {
        type: Object,
        required: true,
      },
    },
    computed: {
      allEstablishments() {
        return [].concat(this.establishment.sisters, this.establishment.childs)
        .filter(esta => esta) // avoid null values
        .map(esta => ({value: esta?.esta_id, text: `${esta?.esta_name} (${esta?.esta_id})`, }));
      },
    },
    watch: {
      exportAll() {
        if (!this.exportAll) {
          this.excludes = [];
        }
      },
    },
    data() {
      return {
        exportAll: false,
        excludes: [],
        load: false,
        pos: 0,
        start: formatStringDateBDD(new Date()),
        end: formatStringDateBDD(new Date()),
        stepDownload: this.$t('btn.loading')
      };
    },
    methods: {
      getEstaName(id) {
        return this.allEstablishments.find(esta => esta.value === id)?.text;
      },
      removeFromExclude(id) {
        const index = this.excludes.findIndex(esta_id => esta_id === id);
        if (index !== -1) {
          this.excludes.splice(index, 1);
        }
      },
      async downloadStats() {
        const labelLoading = this.$t('btn.loading') + " "; 

        this.load = true;
        this.pos = 0;

        const esta = {value: this.establishment.esta_id, text: `${this.establishment.esta_nice_name} (${this.establishment.esta_id})`};
        const listEstas = this.exportAll ? [...this.allEstablishments.slice(0).filter(e => !this.excludes.some(id => id === e.value)), esta] : [esta];

        this.stepDownload = labelLoading + this.pos + ' /' + listEstas.length;

        const list = listEstas.map(async esta => {
          try {
            const res = await statForEstablishmentParticipationActivities(esta.value, this.start, this.end);
            return {
              ...res,
              esta_id: esta.value,
              esta_name: esta.text,
            };
          } catch {
            return null;
          } finally {
            this.pos++;
            this.stepDownload = labelLoading + this.pos + ' / ' + listEstas.length;
          }
        });
        const res = await Promise.all(list)

        this.stepDownload = "Finalisation ...";

        const merged = {
          activities_proposed_and_done: [],
          planned_external_activities: [],
          residents_proposed_and_done: [],
          well_being_for_residents : [],
        };

        // Export to xls:
        const zip = new JSZip();
        for (let data of res) {
          zip.file(`Stats - ${data.esta_name}.xlsx`, await ExportStatToExcel(data));

          // Merge all data into one file (if multi esta):
          if (this.exportAll && this.allEstablishments.length > 1) {
            merged.residents_proposed_and_done = merged.residents_proposed_and_done.concat(data.residents_proposed_and_done).sort((a,b) => a.lastname.localeCompare(b.lastname));
            merged.well_being_for_residents = merged.well_being_for_residents.concat(data.well_being_for_residents).sort((a,b) => a.lastname.localeCompare(b.lastname));

            let m = merged.activities_proposed_and_done.concat(data.activities_proposed_and_done);
            merged.activities_proposed_and_done = m.reduce((acc, item) => {
              const found = acc.find(el => el.activity?.toLowerCase()?.trim() === item.activity?.toLowerCase()?.trim());

              if (found) {
                found.proposed += item.proposed;
                found.done += item.done;
              } else {
                acc.push({ ...item });
              }

              return acc;
            }, [])
            merged.activities_proposed_and_done.sort((a,b) => a.activity?.localeCompare(b.activity));

            m = merged.planned_external_activities.concat(data.planned_external_activities);
            merged.planned_external_activities = m.reduce((acc, item) => {
              const found = acc.find(el => el.activity?.toLowerCase()?.trim() === item.activity?.toLowerCase()?.trim());

              if (found) {
                found.total += item.total;
              } else {
                acc.push({ ...item });
              }

              return acc;
            }, [])
            merged.planned_external_activities.sort((a,b) => a.activity?.localeCompare(b.activity));
          }
        };

        // Create merged stats:
        if (this.exportAll && this.allEstablishments.length > 1) {
          zip.file(`Stats - Global.xlsx`, await ExportStatToExcel(merged));
        }

        // Zip them:
        const content = await zip.generateAsync({ type: "blob" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(content);
        a.download = "stats.zip";
        a.click();
        URL.revokeObjectURL(a.href);
        
        this.load = false;
      }
    },
  };
</script>